import { useEffect, useState } from "react";
import { UpdateCompanyModalProps } from "./UpdateCompanyModal";
import {
    CompanyFormState_default,
    CompanyFormState_fromCompany,
    CompanyFormState_toRequest
} from "../../types/forms/CompanyFormState";
import { getCompanyByIdAsync, updateCompanyAsync } from "../../apiClient";
import { showDangerAlert } from "../../common";

function useUpdateCompanyModal(props: UpdateCompanyModalProps) {
    const [formState, setFormState] = useState(CompanyFormState_default());
    const [isLoading, setIsLoadingCompany] = useState(true);
    const [updateRequestInProgress, setUpdateRequestInProgress] = useState(false);

    const disableSubmit = isLoading || updateRequestInProgress;

    useEffect(() => {
        async function fetchCompanyAsync() {
            const fetchResult = await getCompanyByIdAsync(props.companyId);

            if (fetchResult.failed) {
                console.error("Failed to fetch company with id", props.companyId, "Error:", fetchResult.error);
                setIsLoadingCompany(false);
                showDangerAlert(props.resources.get("FailedToFetchCompany") ?? "");
                props.onHide();
                return;
            }

            const formState = CompanyFormState_fromCompany(fetchResult.value);
            setFormState(formState);
            setIsLoadingCompany(false);
        }

        if (props.show) {
            fetchCompanyAsync();
        }
    }, [props.companyId, props.resources, props.onHide, props.show]);

    async function handleSubmitAsync(e: React.FormEvent) {
        e.preventDefault();
        const request = CompanyFormState_toRequest(formState);

        setUpdateRequestInProgress(true);
        const result = await updateCompanyAsync(props.companyId, request);
        setUpdateRequestInProgress(false);

        if (result.failed) {
            console.error(
                "Failed to update company. CompanyId:",
                props.companyId,
                "Request:",
                request,
                "Error:",
                result.error
            );
            const nameErrors = result.error.errors["Name"];

            const nameErrorMessage = nameErrors && nameErrors.length > 0 ? nameErrors[0] : undefined;

            if (!nameErrorMessage) {
                showDangerAlert(props.resources.get("UpdateCompanyError") ?? "");
                return;
            }

            setFormState((prevState) => ({
                ...prevState,
                name: {
                    ...prevState.name,
                    errorMessage: nameErrorMessage
                }
            }));

            return;
        }

        props.onCompanyUpdated(props.companyId);
    }

    function handleNameChange(name: string) {
        setFormState((prevState) => ({
            ...prevState,
            name: { value: name, errorMessage: prevState.name.errorMessage }
        }));
    }

    function handleIsEnergyHarbourCustomerChange(isChecked: boolean) {
        setFormState((prevState) => ({
            ...prevState,
            isEnergyHarbourCustomer: { value: isChecked, errorMessage: prevState.isEnergyHarbourCustomer.errorMessage }
        }));
    }

    return {
        formState,
        isLoading,
        disableSubmit,
        handleNameChange,
        handleIsEnergyHarbourCustomerChange,
        handleSubmitAsync
    };
}

export default useUpdateCompanyModal;
